import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { environment } from '../../../../../../environments/environment';
import {
  ExternalPrefillConfirmation,
  ExternalPrefillFailure,
  ExternalPrefillProvider,
  ExternalPrefillResult
} from '../external-prefill-provider.model';

/**
 * The VIDA confirm provider (DT-6472 FR-2, FR-4): posts the slug to OUR program-scoped confirm
 * endpoint and maps the outcome to the discriminated union.
 *
 * G8 discipline mirrors the backend's: `ExternalPrefillResult.detail` is DELIBERATELY NEVER SET —
 * the shell renders that slot verbatim into the DOM, and nothing provider-authored (VIDA's
 * `detail` prose, exception messages, outbound URLs) may reach it. Every failure carries only an
 * i18n key.
 */
@Injectable({ providedIn: 'root' })
export class VidaPrefillProvider implements ExternalPrefillProvider {

  readonly key = 'VIDA';
  readonly idLabelKey = 'project.create.external-prefill.vida.label';
  readonly idPlaceholderKey = 'project.create.external-prefill.vida.placeholder';
  readonly introKey = 'project.create.external-prefill.vida.intro';

  /** The backend's failure vocabulary — anything else collapses to PROVIDER_ERROR. */
  private static readonly KNOWN_FAILURES: ExternalPrefillFailure[] = [
    'NOT_FOUND', 'ALREADY_CLAIMED', 'TYPE_NOT_ELIGIBLE',
    'UNMAPPABLE_REGION', 'PROVIDER_ERROR', 'INVALID_APPLICATION'
  ];

  constructor(private readonly httpClient: HttpClient) {}

  async confirm(externalId: string, programId: string): Promise<ExternalPrefillResult> {
    const url = environment.serverUri
      + `api/${environment.apiVersion}/programs/${programId}/external-prefill/VIDA`;
    try {
      const response = await firstValueFrom(this.httpClient.post<{
        token: string;
        externalId: string;
        patch: Record<string, unknown>;
        raw: unknown;
        workflowPayload?: unknown;
      }>(url, { slug: externalId }));
      const confirmation: ExternalPrefillConfirmation = {
        token: response.token,
        externalId: response.externalId,
        patch: response.patch ?? {},
        raw: response.raw,
        workflowPayload: response.workflowPayload
      };
      return { outcome: 'CONFIRMED', confirmation };
    } catch (error) {
      const failure = VidaPrefillProvider.toFailure(error);
      // NO detail — ever. The shell renders that slot into the DOM (see the class doc).
      return {
        outcome: failure,
        messageKey: `project.create.external-prefill.vida.error.${failure}`
      };
    }
  }

  private static toFailure(error: unknown): ExternalPrefillFailure {
    if (error instanceof HttpErrorResponse) {
      const code = (error.error && (error.error.code ?? error.error.failureCode)) as string;
      if (VidaPrefillProvider.KNOWN_FAILURES.includes(code as ExternalPrefillFailure)) {
        return code as ExternalPrefillFailure;
      }
    }
    return 'PROVIDER_ERROR';
  }
}
